import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ FIN-01 and FIN-03 as built at v1.947. A prepay invoice is drafted by accepting a quote
// (api/quotes.ts), so there is no create here: Office issues the draft, and records the payment.

export type Invoice = components['schemas']['InvoiceView'];
export type InvoiceSummary = components['schemas']['InvoiceSummaryView'];
export type PaymentMethod = components['schemas']['PaymentView']['method'];

export const PAYMENT_METHODS: ReadonlyArray<readonly [PaymentMethod, string]> = [
  ['bank_transfer', 'Bank transfer'],
  ['card', 'Card'],
  ['cash', 'Cash'],
  ['other', 'Other'],
];

export interface PaymentInput {
  readonly amountMinor: string;
  readonly receivedOn: string;
  readonly method: PaymentMethod;
  readonly reference: string | null;
}

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

export const invoicesQuery = queryOptions({
  queryKey: ['invoices'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/invoices');
    if (error) throw problem(error, 'Could not load the invoices.');
    return data;
  },
});

/** The job's invoice, if it has one: a prepay job's from acceptance, an account job's from check-in. */
export const jobInvoiceQuery = (jobId: string) =>
  queryOptions({
    queryKey: ['invoices', { jobId }],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/invoices', {
        params: { query: { jobId } },
      });
      if (error) throw problem(error, 'Could not load the job’s invoice.');
      return data[0] ?? null;
    },
  });

export const invoiceQuery = (id: string) =>
  queryOptions({
    queryKey: ['invoices', id],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/invoices/{id}', { params: { path: { id } } });
      if (error) throw problem(error, 'Could not load that invoice.');
      return data;
    },
  });

/** FIN-18: the number is taken now. With no due date, it is due by the first hire day. */
export async function issueInvoice(id: string, dueDate: string | null) {
  const { data, error } = await api.POST('/v1/invoices/{id}/issue', {
    params: { path: { id } },
    body: dueDate ? { dueDate } : {},
  });
  if (error) throw problem(error, 'Could not issue that invoice.');
  return data;
}

export async function recordPayment(id: string, input: PaymentInput) {
  const { data, error } = await api.POST('/v1/invoices/{id}/payments', {
    params: { path: { id } },
    body: input,
  });
  if (error) throw problem(error, 'Could not record that payment.');
  return data;
}

/** "Draft", "Unpaid", "Paid", "Overdue" — what Office needs to know at a glance. */
export function invoiceState(i: InvoiceSummary, today: string): string {
  if (i.status === 'draft') return 'Draft';
  // FIN-02 and FIN-03 at v1.993: credited in full, settled, or owing all or part.
  if (i.creditedInFull) return 'Credited';
  if (i.paid) return 'Paid';
  if (i.dueDate && i.dueDate < today) return 'Overdue';
  return i.owing && i.owing.amountMinor !== i.total.amountMinor ? 'Part paid' : 'Unpaid';
}

/** "51 824 753 556": an ABN as the ATO prints it. */
export function abnLabel(abn: string): string {
  return abn.replace(/^(\d{2})(\d{3})(\d{3})(\d{3})$/, '$1 $2 $3 $4');
}
